/**
 * Admin API helpers for the build-page expansion systems.
 *
 * Plain async functions (no ApiService method additions) that reuse the shared
 * client via apiService.makeRequest, matching the auth header pattern used by
 * the other admin screens.
 *
 * Endpoints:
 *   /admin/achievements.php          (AdminAchievements)
 *   /admin/expansion_moderation.php  (AdminPerformanceModeration, AdminShopModeration,
 *                                    AdminFactorySpecs, AdminBuildMedia)
 */
import apiService from '../ApiService';

const ACHIEVEMENTS_ENDPOINT = '/admin/achievements.php';
const MODERATION_ENDPOINT = '/admin/expansion_moderation.php';

// ─── Shared helpers ───────────────────────────────────────────────────────────

const toNum = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toNumOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toBool = (v: unknown): boolean => v === true || v === 1 || v === '1';

const toStrOrNull = (v: unknown): string | null =>
  v === null || v === undefined || v === '' ? null : String(v);

async function requestData<T>(endpoint: string, init?: RequestInit): Promise<T> {
  const response = await apiService.makeRequest<T>(apiService.getAdminEndpoint(endpoint), init);
  if (!response.success) {
    throw new Error(response.error || response.message || 'Request failed');
  }
  return (response.data ?? {}) as T;
}

async function postAction<T = Record<string, unknown>>(
  endpoint: string,
  body: Record<string, unknown>
): Promise<T> {
  return requestData<T>(endpoint, {
    method: 'POST',
    headers: apiService.getAuthHeaders(),
    body: JSON.stringify(body),
  });
}

// ─── Achievements: types ──────────────────────────────────────────────────────

export type AchievementCalculationType = 'threshold' | 'rank' | 'percentile';

export interface AdminAchievementDefinition {
  id: number;
  slug: string;
  name: string;
  short_name: string | null;
  description: string | null;
  achievement_class: string;
  category: string;
  calculation_type: AchievementCalculationType;
  metric: string;
  scope_type: string;
  threshold: number | null;
  rank_limit: number | null;
  percentile_limit: number | null;
  minimum_sample_size: number;
  visual_tier: string;
  is_public: boolean;
  is_active: boolean;
  version: number;
  current_count: number;
  total_count: number;
  last_calculated_at: string | null;
  stale: boolean;
}

export interface AdminAchievementDefinitionsPayload {
  definitions: AdminAchievementDefinition[];
  metrics: string[];
  scope_types: string[];
  classes: string[];
}

export interface AdminIssuedAchievement {
  id: number;
  slug: string | null;
  name: string | null;
  short_name: string | null;
  achievement_class: string | null;
  category: string | null;
  calculation_type: string | null;
  metric: string | null;
  status: string | null;
  value: number | null;
  rank: number | null;
  population: number | null;
  percentile: number | null;
  issued_at: string | null;
  effective_at: string | null;
  superseded_at: string | null;
  revoked_at: string | null;
  revocation_reason: string | null;
  evidence: Record<string, unknown> | null;
  vehicle: { id: number; label: string; owner_username: string | null };
}

export interface AchievementDefinitionInput {
  slug: string;
  name: string;
  short_name?: string;
  description?: string;
  achievement_class: string;
  category: string;
  calculation_type: AchievementCalculationType;
  metric: string;
  scope_type: string;
  threshold?: number | null;
  rank_limit?: number | null;
  percentile_limit?: number | null;
  minimum_sample_size?: number;
  visual_tier?: string;
  is_public?: boolean;
}

const normalizeDefinition = (raw: Record<string, unknown>): AdminAchievementDefinition => ({
  id: toNum(raw.id),
  slug: String(raw.slug ?? ''),
  name: String(raw.name ?? ''),
  short_name: toStrOrNull(raw.short_name),
  description: toStrOrNull(raw.description),
  achievement_class: String(raw.achievement_class ?? ''),
  category: String(raw.category ?? ''),
  calculation_type: (raw.calculation_type as AchievementCalculationType) || 'threshold',
  metric: String(raw.metric ?? ''),
  scope_type: String(raw.scope_type ?? 'overall'),
  threshold: toNumOrNull(raw.threshold),
  rank_limit: toNumOrNull(raw.rank_limit),
  percentile_limit: toNumOrNull(raw.percentile_limit),
  minimum_sample_size: toNum(raw.minimum_sample_size),
  visual_tier: String(raw.visual_tier ?? 'standard'),
  is_public: toBool(raw.is_public),
  is_active: toBool(raw.is_active),
  version: toNum(raw.version),
  current_count: toNum(raw.current_count),
  total_count: toNum(raw.total_count),
  last_calculated_at: toStrOrNull(raw.last_calculated_at),
  stale: raw.stale === true,
});

// ─── Achievements: calls ──────────────────────────────────────────────────────

export async function adminGetAchievementDefinitions(): Promise<AdminAchievementDefinitionsPayload> {
  const data = await requestData<{
    definitions?: Record<string, unknown>[];
    metrics?: string[];
    scope_types?: string[];
    classes?: string[];
  }>(`${ACHIEVEMENTS_ENDPOINT}?view=definitions`);
  return {
    definitions: (data.definitions ?? []).map(normalizeDefinition),
    metrics: data.metrics ?? [],
    scope_types: data.scope_types ?? [],
    classes: data.classes ?? [],
  };
}

export async function adminGetIssuedAchievements(
  definitionId: number
): Promise<AdminIssuedAchievement[]> {
  const data = await requestData<{ issued?: AdminIssuedAchievement[] }>(
    `${ACHIEVEMENTS_ENDPOINT}?view=issued&definition_id=${definitionId}`
  );
  return data.issued ?? [];
}

export async function adminSeedDefaultAchievements(): Promise<number> {
  const data = await postAction<{ inserted?: number }>(ACHIEVEMENTS_ENDPOINT, {
    action: 'seed_defaults',
  });
  return toNum(data.inserted);
}

export async function adminSaveAchievementDefinition(
  definition: AchievementDefinitionInput
): Promise<{ id: number; created?: boolean; updated?: boolean }> {
  const data = await postAction<{ id?: number; created?: boolean; updated?: boolean }>(
    ACHIEVEMENTS_ENDPOINT,
    { action: 'save_definition', definition }
  );
  return { id: toNum(data.id), created: data.created, updated: data.updated };
}

export async function adminSetAchievementDefinitionActive(
  definitionId: number,
  isActive: boolean
): Promise<void> {
  await postAction(ACHIEVEMENTS_ENDPOINT, {
    action: 'set_active',
    definition_id: definitionId,
    is_active: isActive,
  });
}

export async function adminRecalculateAchievementDefinition(
  definitionId: number
): Promise<Record<string, unknown>> {
  return postAction(ACHIEVEMENTS_ENDPOINT, {
    action: 'recalculate_definition',
    definition_id: definitionId,
  });
}

export async function adminRecalculateVehicleAchievements(vehicleId: number): Promise<{
  issued: number;
  documentation_score: number | null;
}> {
  const data = await postAction<{ issued?: number; documentation_score?: number | null }>(
    ACHIEVEMENTS_ENDPOINT,
    { action: 'recalculate_vehicle', vehicle_id: vehicleId }
  );
  return {
    issued: toNum(data.issued),
    documentation_score: toNumOrNull(data.documentation_score),
  };
}

export async function adminRevokeAchievement(achievementId: number, reason: string): Promise<void> {
  await postAction(ACHIEVEMENTS_ENDPOINT, {
    action: 'revoke',
    achievement_id: achievementId,
    reason,
  });
}

export async function adminRestoreAchievement(achievementId: number): Promise<void> {
  await postAction(ACHIEVEMENTS_ENDPOINT, {
    action: 'restore',
    achievement_id: achievementId,
  });
}

export async function adminPreviewAchievementDefinition(definitionId: number): Promise<number> {
  const data = await postAction<{ eligible_population?: number }>(ACHIEVEMENTS_ENDPOINT, {
    action: 'preview_definition',
    definition_id: definitionId,
  });
  return toNum(data.eligible_population);
}

// ─── Moderation: types ────────────────────────────────────────────────────────

export interface AdminPendingPerformanceRecord {
  id: number;
  vehicle_id: number;
  record_type: string;
  record_type_label: string;
  title: string | null;
  display_value: string | null;
  unit_primary: string | null;
  value_secondary: number | null;
  unit_secondary: string | null;
  venue_name: string | null;
  date_recorded: string | null;
  verification_status: string;
  shop_name: string | null;
  sheet_image_url: string | null;
  vehicle_label: string;
  owner_username: string | null;
}

export type ShopVerificationStatus = 'unverified' | 'pending' | 'verified' | 'suspended';

export interface AdminModerationShop {
  id: number;
  slug: string;
  name: string;
  city: string | null;
  state: string | null;
  website_url: string | null;
  verification_status: ShopVerificationStatus;
  is_public: boolean;
  created_at: string | null;
  creator_username: string | null;
  build_count: number;
}

export interface AdminContributorDispute {
  id: number;
  vehicle_id: number;
  contributor_type: string;
  shop_id: number | null;
  user_id: number | null;
  display_name: string | null;
  role: string;
  work_description: string | null;
  status: string;
  requested_by: string;
  admin_verified: boolean;
  created_at: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  nickname: string | null;
  owner_username: string | null;
  shop_name: string | null;
}

export interface AdminFactorySpec {
  id: number;
  year: number;
  make: string;
  model: string;
  trim: string;
  power_hp: number | null;
  torque_lbft: number | null;
  weight_lbs: number | null;
  engine: string | null;
  forced_induction: string | null;
  transmission: string | null;
  drivetrain: string | null;
  suspension: string | null;
  brakes: string | null;
  wheels: string | null;
  tires: string | null;
  fuel_system: string | null;
  source: string | null;
  is_estimated: boolean;
}

export interface FactorySpecInput {
  year: number;
  make: string;
  model: string;
  trim?: string;
  power_hp?: number | string | null;
  torque_lbft?: number | string | null;
  weight_lbs?: number | string | null;
  engine?: string | null;
  forced_induction?: string | null;
  transmission?: string | null;
  drivetrain?: string | null;
  source?: string | null;
  is_estimated?: boolean;
}

const normalizeShop = (raw: Record<string, unknown>): AdminModerationShop => ({
  id: toNum(raw.id),
  slug: String(raw.slug ?? ''),
  name: String(raw.name ?? ''),
  city: toStrOrNull(raw.city),
  state: toStrOrNull(raw.state),
  website_url: toStrOrNull(raw.website_url),
  verification_status: (raw.verification_status as ShopVerificationStatus) || 'unverified',
  is_public: toBool(raw.is_public),
  created_at: toStrOrNull(raw.created_at),
  creator_username: toStrOrNull(raw.creator_username),
  build_count: toNum(raw.build_count),
});

const normalizeContributor = (raw: Record<string, unknown>): AdminContributorDispute => ({
  id: toNum(raw.id),
  vehicle_id: toNum(raw.vehicle_id),
  contributor_type: String(raw.contributor_type ?? ''),
  shop_id: toNumOrNull(raw.shop_id),
  user_id: toNumOrNull(raw.user_id),
  display_name: toStrOrNull(raw.display_name),
  role: String(raw.role ?? ''),
  work_description: toStrOrNull(raw.work_description),
  status: String(raw.status ?? ''),
  requested_by: String(raw.requested_by ?? ''),
  admin_verified: toBool(raw.admin_verified),
  created_at: toStrOrNull(raw.created_at),
  year: toNumOrNull(raw.year),
  make: toStrOrNull(raw.make),
  model: toStrOrNull(raw.model),
  nickname: toStrOrNull(raw.nickname),
  owner_username: toStrOrNull(raw.owner_username),
  shop_name: toStrOrNull(raw.shop_name),
});

const normalizeFactorySpec = (raw: Record<string, unknown>): AdminFactorySpec => ({
  id: toNum(raw.id),
  year: toNum(raw.year),
  make: String(raw.make ?? ''),
  model: String(raw.model ?? ''),
  trim: String(raw.trim ?? ''),
  power_hp: toNumOrNull(raw.power_hp),
  torque_lbft: toNumOrNull(raw.torque_lbft),
  weight_lbs: toNumOrNull(raw.weight_lbs),
  engine: toStrOrNull(raw.engine),
  forced_induction: toStrOrNull(raw.forced_induction),
  transmission: toStrOrNull(raw.transmission),
  drivetrain: toStrOrNull(raw.drivetrain),
  suspension: toStrOrNull(raw.suspension),
  brakes: toStrOrNull(raw.brakes),
  wheels: toStrOrNull(raw.wheels),
  tires: toStrOrNull(raw.tires),
  fuel_system: toStrOrNull(raw.fuel_system),
  source: toStrOrNull(raw.source),
  is_estimated: toBool(raw.is_estimated),
});

// ─── Moderation: calls ────────────────────────────────────────────────────────

export async function adminGetPendingPerformanceRecords(): Promise<
  AdminPendingPerformanceRecord[]
> {
  const data = await requestData<{ records?: AdminPendingPerformanceRecord[] }>(
    `${MODERATION_ENDPOINT}?view=performance_pending`
  );
  return data.records ?? [];
}

export async function adminGetModerationShops(): Promise<AdminModerationShop[]> {
  const data = await requestData<{ shops?: Record<string, unknown>[] }>(
    `${MODERATION_ENDPOINT}?view=shops`
  );
  return (data.shops ?? []).map(normalizeShop);
}

export async function adminGetContributorDisputes(): Promise<AdminContributorDispute[]> {
  const data = await requestData<{ contributors?: Record<string, unknown>[] }>(
    `${MODERATION_ENDPOINT}?view=contributor_disputes`
  );
  return (data.contributors ?? []).map(normalizeContributor);
}

export async function adminGetFactorySpecs(
  make?: string,
  model?: string
): Promise<AdminFactorySpec[]> {
  const qs = new URLSearchParams({ view: 'factory_specs' });
  if (make) qs.set('make', make);
  if (model) qs.set('model', model);
  const data = await requestData<{ specs?: Record<string, unknown>[] }>(
    `${MODERATION_ENDPOINT}?${qs.toString()}`
  );
  return (data.specs ?? []).map(normalizeFactorySpec);
}

export async function adminVerifyPerformanceRecord(
  recordId: number,
  status: 'admin_verified' | 'owner_reported' | 'evidence_attached' = 'admin_verified'
): Promise<void> {
  await postAction(MODERATION_ENDPOINT, {
    action: 'verify_record',
    record_id: recordId,
    status,
  });
}

export async function adminInvalidatePerformanceRecord(recordId: number): Promise<void> {
  await postAction(MODERATION_ENDPOINT, {
    action: 'invalidate_record',
    record_id: recordId,
  });
}

export async function adminSetShopStatus(
  shopId: number,
  verificationStatus: ShopVerificationStatus
): Promise<void> {
  await postAction(MODERATION_ENDPOINT, {
    action: 'shop_status',
    shop_id: shopId,
    verification_status: verificationStatus,
  });
}

export async function adminSetContributorVerified(
  contributorId: number,
  adminVerified: boolean
): Promise<void> {
  await postAction(MODERATION_ENDPOINT, {
    action: 'contributor_verify',
    contributor_id: contributorId,
    admin_verified: adminVerified,
  });
}

export async function adminRevokeContributorClaim(contributorId: number): Promise<void> {
  await postAction(MODERATION_ENDPOINT, {
    action: 'contributor_revoke',
    contributor_id: contributorId,
  });
}

export async function adminHideMediaImage(imageId: number): Promise<boolean> {
  const data = await postAction<{ hidden?: boolean }>(MODERATION_ENDPOINT, {
    action: 'hide_media',
    image_id: imageId,
  });
  return Boolean(data.hidden);
}

export async function adminSaveFactorySpec(
  spec: FactorySpecInput
): Promise<{ id: number; created?: boolean; updated?: boolean }> {
  const data = await postAction<{ id?: number; created?: boolean; updated?: boolean }>(
    MODERATION_ENDPOINT,
    { action: 'save_factory_spec', spec }
  );
  return { id: toNum(data.id), created: data.created, updated: data.updated };
}

export async function adminDeleteFactorySpec(specId: number): Promise<void> {
  await postAction(MODERATION_ENDPOINT, {
    action: 'delete_factory_spec',
    spec_id: specId,
  });
}
